/* eslint-disable react/prop-types */
import { useState, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
  alpha,
  Dialog,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
} from "@mui/material";
import {
  People,
  Search,
  Clear,
  Refresh,
  CheckCircle,
  Cancel,
  Badge,
  PersonAdd,
  Phone,
  Edit,
  Delete,
  Close,
  Save,
  Warning,
  Person,
  DirectionsBike,
  Numbers,
  ColorLens,
  Info,
} from "@mui/icons-material";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";
import useUsers from "../../hooks/useUsers";
import { DOCUMENT_TYPES } from "shared/constants/documentTypes";

import {
  IdCell,
  IdentificationCell,
  NameCell,
  PhoneCell,
  StatusCell,
  DateCell,
  ActionsCell,
  BikeIdCell,
  BikeCodeCell,
  BikeColorCell,
  BikeActionsCell,
} from "./tableCells";

// ============ OPCIONES DE ESTADO ============
const STATUS_OPTIONS = [
  { value: 1, label: "Activo" },
  { value: 0, label: "Inactivo" },
];

// ============ COMPONENTE PRINCIPAL ============
const UsersTable = ({ refreshFlag = false }) => {
  const {
    users,
    bikes,
    loading,
    error,
    loadUsers,
    updateUser,
    deleteUser,
    updateBike,
    deleteBike,
    totalUsers,
    activeUsers,
    inactiveUsers,
  } = useUsers(refreshFlag);

  const [searchQuery, setSearchQuery] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Modal editar usuario
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    identification: "",
    id_type: 1,
    names: "",
    lastnames: "",
    phone: "",
    status: 1,
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  // Modal eliminar usuario
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deletingLoading, setDeletingLoading] = useState(false);

  // Modal bicicletas del usuario
  const [openBikesModal, setOpenBikesModal] = useState(false);
  const [bikesUser, setBikesUser] = useState(null);
  const [userBikes, setUserBikes] = useState([]);

  // Modal editar bicicleta
  const [openEditBikeModal, setOpenEditBikeModal] = useState(false);
  const [editingBike, setEditingBike] = useState(null);
  const [editBikeFormData, setEditBikeFormData] = useState({
    code: "",
    color: "",
  });
  const [savingEditBike, setSavingEditBike] = useState(false);
  const [editBikeError, setEditBikeError] = useState(null);

  // Modal eliminar bicicleta
  const [openDeleteBikeModal, setOpenDeleteBikeModal] = useState(false);
  const [deletingBike, setDeletingBike] = useState(null);
  const [deletingBikeLoading, setDeletingBikeLoading] = useState(false);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // ========== MAPA DE CONTEO DE BICIS POR USUARIO ==========
  const bikesCountByUser = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(bikes)) return map;

    bikes.forEach((b) => {
      const key = String(b.id_user);
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [bikes]);

  // Actualizar lista de bicis del usuario cuando cambia el modal
  useEffect(() => {
    if (bikesUser && openBikesModal) {
      const userBikesList = bikes.filter((b) => String(b.id_user) === String(bikesUser.id));
      setUserBikes(userBikesList);
    }
  }, [bikesUser, openBikesModal, bikes]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter((u) =>
      [u.identification, u.names, u.lastnames, String(u.id), u.more, u.phone]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q))
    );
  }, [users, searchQuery]);

  // ========== ABRIR MODAL EDITAR USUARIO ==========
  const handleOpenEdit = useCallback((user) => {
    setEditingUser(user);
    setEditFormData({
      identification: user?.identification || "",
      id_type: Number(user?.id_type) || 1,
      names: user?.names || "",
      lastnames: user?.lastnames || "",
      phone: user?.more || user?.phone || "",
      status: Number(user?.status) === 1 ? 1 : 0,
    });
    setEditError(null);
    setOpenEditModal(true);
  }, []);

  const handleCloseEdit = () => {
    setOpenEditModal(false);
    setEditingUser(null);
    setEditError(null);
  };

  const handleEditChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editingUser?.id) return;

    if (!editFormData.identification?.trim()) {
      setEditError("La identificación es requerida");
      return;
    }
    if (!editFormData.names?.trim()) {
      setEditError("El nombre es requerido");
      return;
    }
    if (!editFormData.lastnames?.trim()) {
      setEditError("El apellido es requerido");
      return;
    }

    setSavingEdit(true);
    setEditError(null);

    try {
      const payload = {
        id: editingUser.id,
        identification: editFormData.identification.trim(),
        id_type: Number(editFormData.id_type) || 1,
        names: editFormData.names.trim(),
        lastnames: editFormData.lastnames.trim(),
        status: Number(editFormData.status),
        phone: editFormData.phone?.trim() || "",
      };

      const result = await updateUser(payload);

      if (result?.success) {
        showSnackbar("Usuario actualizado correctamente", "success");
        handleCloseEdit();
      } else {
        setEditError(result?.error || "Error al actualizar usuario");
      }
    } catch (err) {
      setEditError(err.message || "Error al actualizar usuario");
    } finally {
      setSavingEdit(false);
    }
  };

  // ========== ABRIR MODAL ELIMINAR USUARIO ==========
  const handleOpenDelete = useCallback((user) => {
    setDeletingUser(user);
    setOpenDeleteModal(true);
  }, []);

  const handleCloseDelete = () => {
    setOpenDeleteModal(false);
    setDeletingUser(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingUser?.id) return;

    setDeletingLoading(true);

    try {
      const result = await deleteUser(deletingUser.id);

      if (result?.success) {
        showSnackbar(
          `Usuario "${deletingUser.names} ${deletingUser.lastnames}" eliminado`,
          "success"
        );
        handleCloseDelete();
      } else {
        showSnackbar(result?.error || "Error al eliminar usuario", "error");
      }
    } catch (err) {
      showSnackbar(err.message || "Error al eliminar usuario", "error");
    } finally {
      setDeletingLoading(false);
    }
  };

  // ========== ABRIR MODAL BICICLETAS ==========
  const handleOpenBikes = useCallback(
    (user) => {
      setBikesUser(user);
      const userBikesList = bikes.filter((b) => String(b.id_user) === String(user.id));
      setUserBikes(userBikesList);
      setOpenBikesModal(true);
    },
    [bikes]
  );

  const handleCloseBikes = () => {
    setOpenBikesModal(false);
    setBikesUser(null);
    setUserBikes([]);
  };

  // ========== ABRIR MODAL EDITAR BICICLETA ==========
  const handleOpenEditBike = useCallback((bike) => {
    setEditingBike(bike);
    setEditBikeFormData({
      code: bike?.code || "",
      color: bike?.color || "",
    });
    setEditBikeError(null);
    setOpenEditBikeModal(true);
  }, []);

  const handleCloseEditBike = () => {
    setOpenEditBikeModal(false);
    setEditingBike(null);
    setEditBikeError(null);
  };

  const handleEditBikeChange = (field, value) => {
    setEditBikeFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEditBike = async () => {
    if (!editingBike?.id) return;

    if (!editBikeFormData.code?.trim()) {
      setEditBikeError("El código es requerido");
      return;
    }
    if (!editBikeFormData.color?.trim()) {
      setEditBikeError("El color es requerido");
      return;
    }

    setSavingEditBike(true);
    setEditBikeError(null);

    try {
      const payload = {
        id: editingBike.id,
        code: editBikeFormData.code.trim(),
        color: editBikeFormData.color.trim(),
        id_user: editingBike.id_user || bikesUser?.id,
      };

      const result = await updateBike(payload);

      if (result?.success) {
        showSnackbar("Bicicleta actualizada correctamente", "success");
        handleCloseEditBike();
      } else {
        setEditBikeError(result?.error || "Error al actualizar bicicleta");
      }
    } catch (err) {
      setEditBikeError(err.message || "Error al actualizar bicicleta");
    } finally {
      setSavingEditBike(false);
    }
  };

  // ========== ABRIR MODAL ELIMINAR BICICLETA ==========
  const handleOpenDeleteBike = useCallback((bike) => {
    setDeletingBike(bike);
    setOpenDeleteBikeModal(true);
  }, []);

  const handleCloseDeleteBike = () => {
    setOpenDeleteBikeModal(false);
    setDeletingBike(null);
  };

  const handleConfirmDeleteBike = async () => {
    if (!deletingBike?.id) return;

    setDeletingBikeLoading(true);

    try {
      const result = await deleteBike(deletingBike.id);

      if (result?.success) {
        showSnackbar(`Bicicleta "${deletingBike.code}" eliminada`, "success");
        handleCloseDeleteBike();
      } else {
        showSnackbar(result?.error || "Error al eliminar bicicleta", "error");
      }
    } catch (err) {
      showSnackbar(err.message || "Error al eliminar bicicleta", "error");
    } finally {
      setDeletingBikeLoading(false);
    }
  };

  // ========== WRAPPER PARA ACTIONS CELL ==========
  const renderActionsCell = useCallback(
    (props) => {
      const originalRow = props?.row?.original || props?.row || {};
      const count = bikesCountByUser.get(String(originalRow.id)) || 0;

      return (
        <ActionsCell
          row={props?.row}
          onEdit={handleOpenEdit}
          onDelete={handleOpenDelete}
          onViewBikes={handleOpenBikes}
          bikesCount={count}
        />
      );
    },
    [bikesCountByUser, handleOpenEdit, handleOpenDelete, handleOpenBikes]
  );

  // ========== COLUMNAS USUARIOS ==========
  const columns = [
    { Header: "#", accessor: "id", width: "5%", Cell: IdCell },
    {
      Header: "Identificación",
      accessor: "identification",
      width: "18%",
      Cell: IdentificationCell,
    },
    { Header: "Nombre completo", accessor: "name", width: "22%", Cell: NameCell },
    { Header: "Celular", accessor: "phone", width: "13%", Cell: PhoneCell },
    { Header: "Estado", accessor: "status", width: "10%", Cell: StatusCell },
    { Header: "Creado", accessor: "createdAt", width: "10%", Cell: DateCell },
    {
      Header: "Acciones",
      accessor: "actions",
      width: "22%",
      Cell: renderActionsCell,
    },
  ];

  const rows = filteredUsers.map((u) => ({
    id: u.id,
    identification: u.identification || "",
    id_type: u.id_type,
    name: `${u.names || ""} ${u.lastnames || ""}`.trim() || "—",
    names: u.names,
    lastnames: u.lastnames,
    more: u.more,
    phone: u.more || u.phone || "",
    status: u.status,
    createdAt: u.createdAt,
    updatedAt: u.updatedAt,
  }));

  // ========== COLUMNAS BICICLETAS ==========
  const bikeColumns = [
    { Header: "#", accessor: "id", width: "8%", Cell: BikeIdCell },
    { Header: "Código", accessor: "code", width: "30%", Cell: BikeCodeCell },
    { Header: "Color", accessor: "color", width: "30%", Cell: BikeColorCell },
    {
      Header: "Acciones",
      accessor: "actions",
      width: "32%",
      Cell: (props) => (
        <BikeActionsCell {...props} onEdit={handleOpenEditBike} onDelete={handleOpenDeleteBike} />
      ),
    },
  ];

  const bikeRows = userBikes.map((bike) => ({
    id: bike.id,
    code: bike.code || bike.serie || "",
    color: bike.color || "",
    id_user: bike.id_user,
  }));

  return (
    <>
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          border: "1px solid",
          borderColor: "divider",
          mb: 3,
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            p: 2,
            bgcolor: alpha("#1a237e", 0.04),
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid item xs={12} md={6}>
              <MDBox display="flex" alignItems="center" gap={1.5}>
                <People sx={{ color: "#1a237e", fontSize: 24 }} />
                <MDBox>
                  <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#1a237e" }}>
                    Usuarios Registrados
                  </MDTypography>
                  <MDTypography variant="caption" color="text.secondary">
                    {totalUsers} usuarios en total
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <MDBox
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
              >
                <Chip
                  size="small"
                  label={`${activeUsers} Activos`}
                  sx={{
                    bgcolor: "#e8f5e9",
                    color: "#2e7d32",
                    fontWeight: 600,
                    height: 24,
                    fontSize: "0.7rem",
                  }}
                  icon={<CheckCircle />}
                />
                <Chip
                  size="small"
                  label={`${inactiveUsers} Inactivos`}
                  sx={{
                    bgcolor: "#ffebee",
                    color: "#c62828",
                    fontWeight: 600,
                    height: 24,
                    fontSize: "0.7rem",
                  }}
                  icon={<Cancel />}
                />
                <Tooltip title="Recargar usuarios" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={loadUsers}
                      disabled={loading}
                      sx={{
                        bgcolor: "#ffffff",
                        border: "1px solid",
                        borderColor: "divider",
                        "&:hover": { bgcolor: "#f5f5f5" },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Refresh sx={{ fontSize: 18, color: "#1a237e" }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </MDBox>
            </Grid>
          </Grid>
        </Box>

        {/* BUSCADOR */}
        <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Badge sx={{ color: "#64748b", fontSize: 20 }} />
            <TextField
              size="small"
              placeholder="Buscar por cédula, nombre, celular o ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <Clear sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </MDBox>
        </Box>

        {/* ESTADOS */}
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" sx={{ borderRadius: "10px" }}>
              {error}
            </Alert>
          </Box>
        )}

        {loading && rows.length === 0 && (
          <Box
            sx={{
              p: 4,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={28} />
            <MDTypography variant="body2" color="text.secondary">
              Cargando usuarios...
            </MDTypography>
          </Box>
        )}

        {!loading && rows.length === 0 && !error && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <PersonAdd sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
            <MDTypography variant="body2" color="text.secondary">
              No hay usuarios registrados aún
            </MDTypography>
            <MDTypography variant="caption" color="text.secondary" display="block">
              Los usuarios aparecerán aquí cuando registren una bicicleta
            </MDTypography>
          </Box>
        )}

        {rows.length > 0 && (
          <DataTable
            table={{ columns, rows }}
            isSorted={true}
            entriesPerPage={{ defaultValue: 5, entriesOptions: [5, 10, 20] }}
            showTotalEntries={true}
            noEndBorder
            canSearch={false}
          />
        )}
      </Card>

      {/* ============ MODAL EDITAR USUARIO ============ */}
      <Dialog
        open={openEditModal}
        onClose={handleCloseEdit}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            pb: 2.5,
            background: "linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%)",
            borderBottom: "1px solid",
            borderColor: alpha("#1976d2", 0.15),
            position: "relative",
          }}
        >
          <Box display="flex" alignItems="flex-start" gap={2}>
            <Box
              sx={{
                bgcolor: alpha("#1976d2", 0.15),
                borderRadius: "12px",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Edit sx={{ color: "#1976d2", fontSize: 26 }} />
            </Box>
            <Box flex={1} pr={5}>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#0d47a1", mb: 0.5 }}>
                Editar Usuario
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                Actualiza la información del usuario #{editingUser?.id || ""}
              </MDTypography>
            </Box>
          </Box>

          <IconButton
            onClick={handleCloseEdit}
            size="medium"
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "#64748b",
              bgcolor: "rgba(255,255,255,0.7)",
              "&:hover": { bgcolor: "#fff", color: "#0d47a1" },
            }}
          >
            <Close />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, pt: 3 }}>
          {editError && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: "10px" }}>
              {editError}
            </Alert>
          )}

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Identificación *"
                value={editFormData.identification}
                onChange={(e) => handleEditChange("identification", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Badge sx={{ color: "#5c6bc0", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiInputBase-root": { height: "52px", fontSize: "1rem" } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="edit-id-type-label">Tipo de documento *</InputLabel>
                <Select
                  labelId="edit-id-type-label"
                  value={editFormData.id_type}
                  label="Tipo de documento *"
                  onChange={(e) => handleEditChange("id_type", e.target.value)}
                  sx={{ height: "52px", fontSize: "1rem" }}
                >
                  {DOCUMENT_TYPES.map((doc) => (
                    <MenuItem key={doc.id} value={doc.id}>
                      <strong>{doc.code}</strong> — {doc.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Nombre *"
                value={editFormData.names}
                onChange={(e) => handleEditChange("names", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: "#5c6bc0", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiInputBase-root": { height: "52px", fontSize: "1rem" } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Apellido *"
                value={editFormData.lastnames}
                onChange={(e) => handleEditChange("lastnames", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: "#5c6bc0", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiInputBase-root": { height: "52px", fontSize: "1rem" } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Celular"
                value={editFormData.phone}
                onChange={(e) => handleEditChange("phone", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone sx={{ color: "#5c6bc0", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiInputBase-root": { height: "52px", fontSize: "1rem" } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="edit-status-label">Estado *</InputLabel>
                <Select
                  labelId="edit-status-label"
                  value={editFormData.status}
                  label="Estado *"
                  onChange={(e) => handleEditChange("status", e.target.value)}
                  sx={{ height: "52px", fontSize: "1rem" }}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2.5,
            gap: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "#f8fafc",
          }}
        >
          <MDButton
            onClick={handleCloseEdit}
            variant="outlined"
            startIcon={<Close />}
            disabled={savingEdit}
            sx={{
              px: 4,
              height: "44px",
              borderRadius: "10px",
              fontWeight: 600,
              color: "#64748b",
              borderColor: "#cbd5e1",
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" },
            }}
          >
            Cancelar
          </MDButton>
          <MDButton
            onClick={handleSaveEdit}
            variant="contained"
            startIcon={savingEdit ? <CircularProgress size={16} color="inherit" /> : <Save />}
            disabled={savingEdit}
            sx={{
              px: 4,
              height: "44px",
              borderRadius: "10px",
              fontWeight: 700,
              bgcolor: "#1976d2",
              color: "#ffffff !important",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              "&:hover": { bgcolor: "#1565c0" },
            }}
          >
            {savingEdit ? "Guardando..." : "Guardar Cambios"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ============ MODAL ELIMINAR USUARIO ============ */}
      <Dialog
        open={openDeleteModal}
        onClose={handleCloseDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            pb: 2.5,
            background: "linear-gradient(135deg, #ffebee 0%, #fce4ec 100%)",
            borderBottom: "1px solid",
            borderColor: alpha("#c62828", 0.15),
            position: "relative",
          }}
        >
          <Box display="flex" alignItems="flex-start" gap={2}>
            <Box
              sx={{
                bgcolor: alpha("#c62828", 0.15),
                borderRadius: "12px",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Warning sx={{ color: "#c62828", fontSize: 26 }} />
            </Box>
            <Box flex={1} pr={5}>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#b71c1c", mb: 0.5 }}>
                Eliminar Usuario
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                Esta acción no se puede deshacer
              </MDTypography>
            </Box>
          </Box>

          <IconButton
            onClick={handleCloseDelete}
            size="medium"
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "#64748b",
              bgcolor: "rgba(255,255,255,0.7)",
              "&:hover": { bgcolor: "#fff", color: "#b71c1c" },
            }}
          >
            <Close />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Alert
            severity="error"
            icon={<Warning sx={{ fontSize: 20 }} />}
            sx={{
              mb: 3,
              borderRadius: "10px",
              bgcolor: alpha("#c62828", 0.06),
              border: "1px solid",
              borderColor: alpha("#c62828", 0.15),
            }}
          >
            <MDTypography variant="body2" fontWeight="600">
              ¿Estás seguro de que deseas eliminar este usuario?
            </MDTypography>
          </Alert>

          {deletingUser && (
            <Box
              sx={{
                p: 2.5,
                borderRadius: "12px",
                bgcolor: "#f8fafc",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={5}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="600">
                    ID
                  </MDTypography>
                </Grid>
                <Grid item xs={7}>
                  <MDTypography variant="body2" fontWeight="700" color="info">
                    #{deletingUser.id}
                  </MDTypography>
                </Grid>

                <Grid item xs={5}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="600">
                    Identificación
                  </MDTypography>
                </Grid>
                <Grid item xs={7}>
                  <MDTypography variant="body2" fontWeight="500">
                    {deletingUser.identification || "—"}
                  </MDTypography>
                </Grid>

                <Grid item xs={5}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="600">
                    Nombre completo
                  </MDTypography>
                </Grid>
                <Grid item xs={7}>
                  <MDTypography variant="body2" fontWeight="500">
                    {deletingUser.names} {deletingUser.lastnames}
                  </MDTypography>
                </Grid>

                {(deletingUser.more || deletingUser.phone) && (
                  <>
                    <Grid item xs={5}>
                      <MDTypography variant="caption" color="text.secondary" fontWeight="600">
                        Celular
                      </MDTypography>
                    </Grid>
                    <Grid item xs={7}>
                      <MDTypography variant="body2" fontWeight="500">
                        {deletingUser.more || deletingUser.phone}
                      </MDTypography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2.5,
            gap: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "#f8fafc",
          }}
        >
          <MDButton
            onClick={handleCloseDelete}
            variant="outlined"
            startIcon={<Close />}
            disabled={deletingLoading}
            sx={{
              px: 4,
              height: "44px",
              borderRadius: "10px",
              fontWeight: 600,
              color: "#64748b",
              borderColor: "#cbd5e1",
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" },
            }}
          >
            Cancelar
          </MDButton>
          <MDButton
            onClick={handleConfirmDelete}
            variant="contained"
            startIcon={
              deletingLoading ? <CircularProgress size={16} color="inherit" /> : <Delete />
            }
            disabled={deletingLoading}
            sx={{
              px: 4,
              height: "44px",
              borderRadius: "10px",
              fontWeight: 700,
              bgcolor: "#c62828",
              color: "#ffffff !important",
              boxShadow: "0 4px 12px rgba(198, 40, 40, 0.3)",
              "&:hover": { bgcolor: "#b71c1c" },
            }}
          >
            {deletingLoading ? "Eliminando..." : "Eliminar Usuario"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ============ MODAL BICICLETAS DEL USUARIO ============ */}
      <Dialog
        open={openBikesModal}
        onClose={handleCloseBikes}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            pb: 2.5,
            background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
            borderBottom: "1px solid",
            borderColor: alpha("#009688", 0.15),
            position: "relative",
          }}
        >
          <Box display="flex" alignItems="flex-start" gap={2}>
            <Box
              sx={{
                bgcolor: alpha("#009688", 0.15),
                borderRadius: "12px",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <DirectionsBike sx={{ color: "#00796b", fontSize: 26 }} />
            </Box>
            <Box flex={1} pr={5}>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#00695c", mb: 0.5 }}>
                Bicicletas Asociadas
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                {bikesUser?.names} {bikesUser?.lastnames} — C.C {bikesUser?.identification || "—"}
              </MDTypography>
            </Box>
          </Box>

          <IconButton
            onClick={handleCloseBikes}
            size="medium"
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "#64748b",
              bgcolor: "rgba(255,255,255,0.7)",
              "&:hover": { bgcolor: "#fff", color: "#00695c" },
            }}
          >
            <Close />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, pt: 3 }}>
          {userBikes.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <DirectionsBike sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
              <MDTypography variant="body2" color="text.secondary">
                Este usuario no tiene bicicletas asociadas
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary" display="block">
                Las bicicletas se asocian al registrar un ingreso
              </MDTypography>
            </Box>
          ) : (
            <>
              <Alert
                severity="info"
                icon={<Info sx={{ fontSize: 18 }} />}
                sx={{ mb: 2, borderRadius: "10px" }}
              >
                Este usuario tiene <strong>{userBikes.length}</strong>{" "}
                {userBikes.length === 1 ? "bicicleta registrada" : "bicicletas registradas"}
              </Alert>

              <Box
                sx={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <DataTable
                  table={{ columns: bikeColumns, rows: bikeRows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  canSearch={false}
                />
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2.5,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "#f8fafc",
          }}
        >
          <MDButton
            onClick={handleCloseBikes}
            variant="outlined"
            startIcon={<Close />}
            sx={{
              px: 4,
              height: "44px",
              borderRadius: "10px",
              fontWeight: 600,
              color: "#64748b",
              borderColor: "#cbd5e1",
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" },
            }}
          >
            Cerrar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ============ MODAL EDITAR BICICLETA ============ */}
      <Dialog
        open={openEditBikeModal}
        onClose={handleCloseEditBike}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            pb: 2.5,
            background: "linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%)",
            borderBottom: "1px solid",
            borderColor: alpha("#1976d2", 0.15),
            position: "relative",
          }}
        >
          <Box display="flex" alignItems="flex-start" gap={2}>
            <Box
              sx={{
                bgcolor: alpha("#1976d2", 0.15),
                borderRadius: "12px",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Edit sx={{ color: "#1976d2", fontSize: 26 }} />
            </Box>
            <Box flex={1} pr={5}>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#0d47a1", mb: 0.5 }}>
                Editar Bicicleta
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                Actualiza la información de la bicicleta #{editingBike?.id || ""}
              </MDTypography>
            </Box>
          </Box>

          <IconButton
            onClick={handleCloseEditBike}
            size="medium"
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "#64748b",
              bgcolor: "rgba(255,255,255,0.7)",
              "&:hover": { bgcolor: "#fff", color: "#0d47a1" },
            }}
          >
            <Close />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, pt: 3 }}>
          {editBikeError && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: "10px" }}>
              {editBikeError}
            </Alert>
          )}

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Código / Serie *"
                value={editBikeFormData.code}
                onChange={(e) => handleEditBikeChange("code", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Numbers sx={{ color: "#5c6bc0", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiInputBase-root": { height: "52px", fontSize: "1rem" } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Color *"
                value={editBikeFormData.color}
                onChange={(e) => handleEditBikeChange("color", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ColorLens sx={{ color: "#5c6bc0", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: editBikeFormData.color && (
                    <InputAdornment position="end">
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          bgcolor: editBikeFormData.color.toLowerCase(),
                          border: "1px solid #e5e7eb",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiInputBase-root": { height: "52px", fontSize: "1rem" } }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2.5,
            gap: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "#f8fafc",
          }}
        >
          <MDButton
            onClick={handleCloseEditBike}
            variant="outlined"
            startIcon={<Close />}
            disabled={savingEditBike}
            sx={{
              px: 4,
              height: "44px",
              borderRadius: "10px",
              fontWeight: 600,
              color: "#64748b",
              borderColor: "#cbd5e1",
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" },
            }}
          >
            Cancelar
          </MDButton>
          <MDButton
            onClick={handleSaveEditBike}
            variant="contained"
            startIcon={savingEditBike ? <CircularProgress size={16} color="inherit" /> : <Save />}
            disabled={savingEditBike}
            sx={{
              px: 4,
              height: "44px",
              borderRadius: "10px",
              fontWeight: 700,
              bgcolor: "#1976d2",
              color: "#ffffff !important",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              "&:hover": { bgcolor: "#1565c0" },
            }}
          >
            {savingEditBike ? "Guardando..." : "Guardar Cambios"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ============ MODAL ELIMINAR BICICLETA ============ */}
      <Dialog
        open={openDeleteBikeModal}
        onClose={handleCloseDeleteBike}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            pb: 2.5,
            background: "linear-gradient(135deg, #ffebee 0%, #fce4ec 100%)",
            borderBottom: "1px solid",
            borderColor: alpha("#c62828", 0.15),
            position: "relative",
          }}
        >
          <Box display="flex" alignItems="flex-start" gap={2}>
            <Box
              sx={{
                bgcolor: alpha("#c62828", 0.15),
                borderRadius: "12px",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Warning sx={{ color: "#c62828", fontSize: 26 }} />
            </Box>
            <Box flex={1} pr={5}>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#b71c1c", mb: 0.5 }}>
                Eliminar Bicicleta
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                Esta acción no se puede deshacer
              </MDTypography>
            </Box>
          </Box>

          <IconButton
            onClick={handleCloseDeleteBike}
            size="medium"
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "#64748b",
              bgcolor: "rgba(255,255,255,0.7)",
              "&:hover": { bgcolor: "#fff", color: "#b71c1c" },
            }}
          >
            <Close />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Alert
            severity="error"
            icon={<Warning sx={{ fontSize: 20 }} />}
            sx={{
              mb: 3,
              borderRadius: "10px",
              bgcolor: alpha("#c62828", 0.06),
              border: "1px solid",
              borderColor: alpha("#c62828", 0.15),
            }}
          >
            <MDTypography variant="body2" fontWeight="600">
              ¿Estás seguro de que deseas eliminar esta bicicleta?
            </MDTypography>
          </Alert>

          {deletingBike && (
            <Box
              sx={{
                p: 2.5,
                borderRadius: "12px",
                bgcolor: "#f8fafc",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={5}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="600">
                    ID
                  </MDTypography>
                </Grid>
                <Grid item xs={7}>
                  <MDTypography variant="body2" fontWeight="700" color="info">
                    #{deletingBike.id}
                  </MDTypography>
                </Grid>

                <Grid item xs={5}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="600">
                    Código
                  </MDTypography>
                </Grid>
                <Grid item xs={7}>
                  <MDTypography variant="body2" fontWeight="500">
                    {deletingBike.code || "—"}
                  </MDTypography>
                </Grid>

                <Grid item xs={5}>
                  <MDTypography variant="caption" color="text.secondary" fontWeight="600">
                    Color
                  </MDTypography>
                </Grid>
                <Grid item xs={7}>
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        bgcolor: deletingBike.color ? deletingBike.color.toLowerCase() : "#e0e0e0",
                        border: "1px solid #ccc",
                      }}
                    />
                    <MDTypography variant="body2" fontWeight="500">
                      {deletingBike.color || "—"}
                    </MDTypography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 2.5,
            gap: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "#f8fafc",
          }}
        >
          <MDButton
            onClick={handleCloseDeleteBike}
            variant="outlined"
            startIcon={<Close />}
            disabled={deletingBikeLoading}
            sx={{
              px: 4,
              height: "44px",
              borderRadius: "10px",
              fontWeight: 600,
              color: "#64748b",
              borderColor: "#cbd5e1",
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" },
            }}
          >
            Cancelar
          </MDButton>
          <MDButton
            onClick={handleConfirmDeleteBike}
            variant="contained"
            startIcon={
              deletingBikeLoading ? <CircularProgress size={16} color="inherit" /> : <Delete />
            }
            disabled={deletingBikeLoading}
            sx={{
              px: 4,
              height: "44px",
              borderRadius: "10px",
              fontWeight: 700,
              bgcolor: "#c62828",
              color: "#ffffff !important",
              boxShadow: "0 4px 12px rgba(198, 40, 40, 0.3)",
              "&:hover": { bgcolor: "#b71c1c" },
            }}
          >
            {deletingBikeLoading ? "Eliminando..." : "Eliminar Bicicleta"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

UsersTable.propTypes = {
  refreshFlag: PropTypes.bool,
};

UsersTable.defaultProps = {
  refreshFlag: false,
};

export default UsersTable;
/* eslint-enable react/prop-types */
